// @dynamic-i18n-keys
// Dynamic t() at lines 67–69, 83–84, 98–100 (itemType: ShopItemType):
//   pages.shop.uploadMore.var.adoptables.title
//   pages.shop.uploadMore.var.adoptables.subtitle
//   pages.shop.uploadMore.var.adoptables.subtitle.mobile
//   pages.shop.uploadMore.var.commissions.title
//   pages.shop.uploadMore.var.commissions.subtitle
//   pages.shop.uploadMore.var.commissions.subtitle.mobile
//   pages.shop.uploadMore.var.originals.title
//   pages.shop.uploadMore.var.originals.subtitle
//   pages.shop.uploadMore.var.originals.subtitle.mobile
//   pages.shop.uploadMore.var.premium_content.title
//   pages.shop.uploadMore.var.premium_content.subtitle
//   pages.shop.uploadMore.var.premium_content.subtitle.mobile
//   pages.shop.uploadMore.var.premium_folders.title
//   pages.shop.uploadMore.var.premium_folders.subtitle
//   pages.shop.uploadMore.var.premium_folders.subtitle.mobile
//   pages.shop.uploadMore.nonCore.var.adoptables.title
//   pages.shop.uploadMore.nonCore.var.adoptables.subtitle
//   pages.shop.uploadMore.nonCore.var.commissions.title
//   pages.shop.uploadMore.nonCore.var.commissions.subtitle
//   pages.shop.uploadMore.nonCore.var.originals.title
//   pages.shop.uploadMore.nonCore.var.originals.subtitle
//   pages.shop.uploadMore.nonCore.var.premium_content.title
//   pages.shop.uploadMore.nonCore.var.premium_content.subtitle
//   pages.shop.uploadMore.nonCore.var.premium_folders.title
//   pages.shop.uploadMore.nonCore.var.premium_folders.subtitle
import type { ReactNode, FC } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { URLS } from '@wix/da-url';
import { BiData, BiEvent, BuyCoreButtonBiEvent } from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import BuyCoreBrandedButton from '@wix/da-shared-react/pkg/Button/core/dsPresets/BuyCoreBrandedButton';
import CoreExclusiveFeature from '@wix/da-shared-react/pkg/indicators/CoreExclusiveFeature/CoreExclusiveFeature';
import EmptyState from '../../../../EmptyState';
import { ShopItemType } from '../../../../../../types/shop';
import AddItemCard from '../AddItemCard';

import s from './UploadMore.scss';

export interface FolderUploadMore {
  uploadMore: ReactNode;
}

export interface Props {
  hasCoreMembership?: boolean;
  itemType: ShopItemType;
  height?: number;
  onCreate?: () => void;
  biData?: BiEvent;
  view?: 'large' | 'small';
}

export const UploadMore: FC<Props> = ({
  hasCoreMembership,
  itemType,
  height = 256,
  onCreate,
  biData,
  view = 'large',
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  let requiresCoreMembership, referralInfo;

  switch (itemType) {
    case ShopItemType.ADOPTABLES:
      referralInfo = ReferralType.SHOP_ADOPTABLES;
      requiresCoreMembership = true;
      break;

    case ShopItemType.PREMIUM_CONTENT:
      referralInfo = ReferralType.SHOP_PREMIUM_DOWNLOADS;
      requiresCoreMembership = true;
      break;

    case ShopItemType.PREMIUM_FOLDERS:
      referralInfo = ReferralType.SHOP_PREMIUM_GALLERIES;
      requiresCoreMembership = true;
      break;

    default:
      requiresCoreMembership = false;
  }

  // this tells mobile users that they need to switch to desktop
  // to upload if it's a CORE feature like galleries or downloads
  if (requiresCoreMembership && isMobile) {
    return (
      <EmptyState
        className={classnames(s['root'], height > 400 && s['tall'])}
        title={t(`pages.shop.uploadMore.var.${itemType}.title`)}
        titleClassName={s['title']}
        subtitle={t(`pages.shop.uploadMore.var.${itemType}.subtitle.mobile`)}
        subtitleClassName={s['subtitle']}
        illustration={<CoreExclusiveFeature withoutBuyCTA />}
        illustrationClassName={s['exclusive']}
        view={'small'}
        height={height}
      />
    );
  }

  // add item that doesnt require CORE membership or user already has CORE
  if (!requiresCoreMembership || hasCoreMembership) {
    return (
      <AddItemCard
        title={t(`pages.shop.uploadMore.var.${itemType}.title`)}
        subtitle={t(`pages.shop.uploadMore.var.${itemType}.subtitle`)}
        onClick={onCreate}
        biData={biData}
        height={view === 'small' ? undefined : height}
        autoWidth={true}
        view={view}
      />
    );
  }

  // non-core
  return (
    <EmptyState
      className={classnames(s['root'], height > 400 && s['tall'])}
      title={t(`pages.shop.uploadMore.nonCore.var.${itemType}.title`)}
      titleClassName={s['title']}
      subtitle={t(`pages.shop.uploadMore.nonCore.var.${itemType}.subtitle`)}
      subtitleClassName={s['subtitle']}
      illustration={<CoreExclusiveFeature withoutBuyCTA />}
      illustrationClassName={s['exclusive']}
      withButton={true}
      customButton={
        <BuyCoreBrandedButton
          size="large"
          href={URLS.CORE_MEMBERSHIP}
          biData={BiData<BuyCoreButtonBiEvent>({
            evid: 375,
            referral_info: referralInfo,
          })}
          withSaleSparkles
        >
          {t('widgets.tierContent.empty.cta.getCore')}
        </BuyCoreBrandedButton>
      }
      view={'small'}
    />
  );
};

UploadMore.displayName = 'UploadMore';

export default UploadMore;
